/**
 * Application Configuration
 *
 * This file provides environment-aware configuration for the frontend.
 * It automatically detects whether the app is running in production or development
 * based on the hostname and provides the appropriate API URL.
 */

/**
 * Determines the API base URL based on the current environment
 *
 * Logic:
 * - Production: bringstomind.com → API at same domain (handled by nginx proxy)
 * - Development: dev.bringstomind.com → API at same domain (handled by nginx proxy)
 * - Local: localhost or 192.168.x.x → API at explicitly configured backend URL
 *
 * @returns {string} The base URL for API requests (without trailing slash)
 */
export function getApiBaseUrl() {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Production: bringstomind.com
  if (hostname === 'bringstomind.com' || hostname === 'www.bringstomind.com') {
    return `${protocol}//${hostname}`;
  }

  // Development: dev.bringstomind.com
  if (hostname === 'dev.bringstomind.com') {
    return `${protocol}//${hostname}`;
  }

  // Local development with explicit port
  if (hostname === 'localhost' || hostname.startsWith('192.168.') || hostname.startsWith('127.0.')) {
    // If accessing via port 3001 (React dev server), backend is on port 3000
    if (port === '3001') {
      return `${protocol}//localhost:3000`;
    }
    // If accessing via port 7443 (Docker nginx), use that
    if (port === '7443' || port === '7080') {
      return `${protocol}//${hostname}:${port}`;
    }
    // Default: same host and port (assume nginx proxy setup)
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  // Fallback: same origin
  console.warn('Unknown hostname:', hostname, '- using same-origin API');
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
}

/**
 * API Base URL - Use this for all API requests
 *
 * Example usage:
 *   import { API_BASE_URL } from './config';
 *   fetch(`${API_BASE_URL}/api/auth/login`, { ... });
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Environment name for debugging/logging
 */
export const ENVIRONMENT = (() => {
  const hostname = window.location.hostname;
  const port = window.location.port;

  if (hostname === 'bringstomind.com' || hostname === 'www.bringstomind.com') {
    return 'production';
  }
  if (hostname === 'dev.bringstomind.com') {
    return 'development';
  }
  // Dev environment ports (6080 HTTP, 6443 HTTPS)
  if (port === '6080' || port === '6443') {
    return 'development';
  }
  // Prod environment ports (7080 HTTP, 7443 HTTPS)
  if (port === '7080' || port === '7443') {
    return 'production';
  }
  return 'local';
})();

/**
 * Check if running in production environment
 */
export const IS_PRODUCTION = ENVIRONMENT === 'production';

/**
 * Check if running in development environment
 */
export const IS_DEVELOPMENT = ENVIRONMENT === 'development';

/**
 * Check if running locally
 */
export const IS_LOCAL = ENVIRONMENT === 'local';

// Log configuration on startup (only in non-production)
if (!IS_PRODUCTION) {
  console.log('🔧 App Configuration:', {
    environment: ENVIRONMENT,
    apiBaseUrl: API_BASE_URL,
    hostname: window.location.hostname,
    port: window.location.port
  });
}
